import { supabase } from "@/integrations/supabase/client";

// Klaviyo client-side subscription helper.
// Uses the public Site ID (company_id) only — no private API key.
// Endpoint docs: https://developers.klaviyo.com/en/reference/create_client_subscription

export const KLAVIYO_COMPANY_ID = "UAE9w6";
export const KLAVIYO_PRIVATE_LIST_ID = "T4uLV9";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const isValidEmail = (email: string) => EMAIL_RE.test(email.trim());

export interface KlaviyoSubscribeOptions {
  email: string;
  source?: string;
  signupLocation?: string;
  formName?: string;
  extraProperties?: Record<string, unknown>;
}

/**
 * Subscribes an email to a Klaviyo list with marketing consent.
 * Returns silently on success (including already-subscribed emails — Klaviyo
 * returns 202 in both cases). Throws on validation or network failure.
 */
export async function klaviyoSubscribe({
  email,
  source = "homepage_private_list",
  signupLocation = "homepage",
  formName = "Join the Private List",
  extraProperties = {},
}: KlaviyoSubscribeOptions): Promise<void> {
  if (!isValidEmail(email)) {
    throw new Error("invalid_email");
  }

  const url = `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(
    KLAVIYO_COMPANY_ID,
  )}`;

  const body = {
    data: {
      type: "subscription",
      attributes: {
        custom_source: source,
        profile: {
          data: {
            type: "profile",
            attributes: {
              email: email.trim(),
              subscriptions: {
                email: {
                  marketing: {
                    consent: "SUBSCRIBED",
                  },
                },
              },
              properties: {
                source,
                signup_location: signupLocation,
                brand: "MOTAI",
                form_name: formName,
                ...extraProperties,
              },
            },
          },
        },
      },
      relationships: {
        list: { data: { type: "list", id: KLAVIYO_PRIVATE_LIST_ID } },
      },
    },
  };

  let res: Response;
  try {
    res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/vnd.api+json",
        accept: "application/vnd.api+json",
        revision: "2026-04-15",
      },
      body: JSON.stringify(body),
    });
  } catch {
    const { error } = await supabase.functions.invoke("klaviyo-subscribe", {
      body: { email, source, signupLocation, formName, extraProperties },
    });

    if (!error) return;
    throw new Error(error.message || "klaviyo_request_failed");
  }

  // Klaviyo returns 202 Accepted on success (including already-subscribed).
  // Treat any 2xx as success.
  if (res.status >= 200 && res.status < 300) return;

  // 409 = profile already exists subscribed — treat as success.
  if (res.status === 409) return;

  throw new Error(`klaviyo_request_failed_${res.status}`);
}